// The Amazon overview's own nav, for InRange and Amazon alike: the agents app's sidebar with
// the overview's pages and nothing else, collapsing to an icon rail
// (docs/Y2026-Q4/design-amazon-item-owners.md section 2.1).
import { useContext, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { UserContext } from "../../auth";
import { Tooltip } from "../../components/Tooltip";
import { NavIcon } from "../../layouts/NavIcon";
import { useIsMobile } from "../../utils/useIsMobile";
import { carriedSearch, OVERVIEW_PAGES, pagePath } from "./overviewPages";
import styles from "../../layouts/AppLayout.module.css";
import overview from "./amazonOverview.module.css";

const COLLAPSED_KEY = "inrange-agents.overview-nav.collapsed";

// A per-viewer convenience: unreadable storage just means expanded.
const storedCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

export const OverviewNav = () => {
  const { search } = useLocation();
  const isMobile = useIsMobile();
  // InRange's way back to the agents app; never shown to Amazon, nor in View as.
  const staff = useContext(UserContext).role === "admin";
  const [collapsed, setCollapsed] = useState(storedCollapsed);
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      window.localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
    } catch {
      // Storage unavailable: the toggle still works for this page load.
    }
  };
  const to = (id: (typeof OVERVIEW_PAGES)[number]["id"]) => ({
    pathname: pagePath(id),
    search: carriedSearch(search),
  });

  if (collapsed || isMobile)
    return (
      <aside className={styles.rail} aria-label="Amazon overview">
        {!isMobile && (
          <button
            type="button"
            className={styles.collapseButton}
            onClick={toggle}
            aria-label="Expand navigation"
            aria-expanded={false}
            title="Expand navigation"
          >
            <span aria-hidden>»</span>
          </button>
        )}
        <nav className={styles.railNav}>
          {OVERVIEW_PAGES.map((p) => (
            <Tooltip key={p.id} content={p.label} placement="bottom" align="left">
              <NavLink
                to={to(p.id)}
                aria-label={p.label}
                className={({ isActive }) =>
                  `${styles.railItem} ${isActive ? styles.itemActive : ""}`
                }
              >
                <NavIcon name={p.icon} />
              </NavLink>
            </Tooltip>
          ))}
        </nav>
        {staff && (
          <div className={styles.railFoot}>
            <Tooltip content="InRange Agents" placement="top" align="left">
              <Link to="/" aria-label="InRange Agents" className={styles.railSwitch}>
                IA
              </Link>
            </Tooltip>
          </div>
        )}
      </aside>
    );

  return (
    <aside className={styles.sidebar} aria-label="Amazon overview">
      <div className={styles.brandRow}>
        <div className={styles.brand}>
          <span className={overview.navMark}>Amazon UK × InRange</span>
        </div>
        <button
          type="button"
          className={styles.collapseButton}
          onClick={toggle}
          aria-label="Collapse navigation"
          aria-expanded
          title="Collapse navigation"
        >
          <span aria-hidden>«</span>
        </button>
      </div>
      <nav className={styles.nav}>
        {OVERVIEW_PAGES.map((p) => (
          <NavLink
            key={p.id}
            to={to(p.id)}
            className={({ isActive }) => `${styles.item} ${isActive ? styles.itemActive : ""}`}
          >
            <span className={styles.itemIcon} aria-hidden>
              <NavIcon name={p.icon} />
            </span>
            {p.label}
          </NavLink>
        ))}
      </nav>
      {staff && (
        <div className={styles.foot}>
          <Link to="/" className={styles.appSwitch}>
            <span aria-hidden>←</span>
            InRange Agents
          </Link>
        </div>
      )}
    </aside>
  );
};
